import { useHomeData } from '@/hooks/useHomeData';
import { useSacola } from '@/hooks/useSacola';
import { useSnackbar } from '@/hooks/useSnackbar';
import { recuperarConteudoRecomendacaoEspecifica } from '@/services/conteudoService';
import { registrarEvento } from '@/services/googleAnalytics';
import { recuperarProduto } from '@/services/marketplaceService';
import { ConteudoRecomendacao } from '@/services/types/recomendacao';
import { ImagensProdutoDestaqueTipo } from '@/types/imagensProdutoDestaque';
import { ImagensDestaqueProduto, PlanoProduto, Produto } from '@/types/produto';
import { AppRouterInstance } from 'next/dist/shared/lib/app-router-context.shared-runtime';
import { StaticImageData } from 'next/image';
import { useEffect, useMemo, useRef, useState } from 'react';

/*  DISCOS DEEZER */
import disco1 from '@/assets/deezer_disco_1.jpg';
import disco2 from '@/assets/deezer_disco_2.jpg';
import disco3 from '@/assets/deezer_disco_3.jpg';
import disco4 from '@/assets/deezer_disco_4.jpg';
import disco5 from '@/assets/deezer_disco_5.jpg';
import { deezerID } from '@/data/assinaturaID';
import { useMetaTrackHook } from '@/hooks/useMetaTrackHook';
import { useDesactivateOfferRedirect } from '@/hooks/useDesactivateOfferRedirect';
import assinaturasOverride from '@/data/assinaturasOverride';

function useServicoAssinaturaPageHook(
  $assinaturaSlug: string,
  router: AppRouterInstance,
) {
  const { assinaturas } = useHomeData();
  const { itens, adicionarItemASacola, existeComboNaSacola } = useSacola();
  const { mostrarSnackbar } = useSnackbar();

  const [assinatura, setAssinatura] = useState<Produto | null>(null);
  const [carregandoAssinatura, setCarregandoAssinatura] = useState(false);

  const planosAssinatura: PlanoProduto[] = useMemo(() => {
    if (!assinatura || !assinatura.planos || assinatura.planos.length === 0)
      return [];
    return [...assinatura.planos].sort((a, b) => a.preco - b.preco);
  }, [assinatura]);

  const [tipoAssinatura, setTipoAssintura] = useState<string>('Mensal');

  // const [produtoRelacionados, setProdutoRelacionados] = useState<
  //   Produto[] | null
  // >(null);

  // const [combosComOProduto, setCombosComOProduto] = useState<Combo[] | null>(
  //   null,
  // );

  // async function recuperarProdutosRelacionados(assinatura: Produto) {
  //   const produtosRelacionadosFromServer = await listarProdutos({
  //     filtros: `categoria.nome.Contains("${assinatura?.categoria?.nome}") && somenteBeneficio == false`,
  //   });
  //   setProdutoRelacionados(
  //     produtosRelacionadosFromServer.filter(
  //       (produtoRelacionado) => produtoRelacionado.id !== assinatura.id,
  //     ),
  //   );
  // }

  // async function recuperarCombosComOProduto(assinaturaID: string) {
  //   const combosComOProdutoFromServer =
  //     await listarCombosComProduto(assinaturaID);
  //   setCombosComOProduto(combosComOProdutoFromServer);
  // }

  const { recuperarQueryStringDaUrl, enviarEventosMetaTrack } =
    useMetaTrackHook();

  const jaExecutouRecuperacaoQueryStringData = useRef(false);

  useEffect(() => {
    if (jaExecutouRecuperacaoQueryStringData.current) return;
    jaExecutouRecuperacaoQueryStringData.current = true;

    recuperarQueryStringDaUrl();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const aplicarOverrideAdmin = (produto: Produto): Produto => {
    const dadosOverride = (
      assinaturasOverride as unknown as Partial<Produto>[]
    ).find(
      (override) =>
        override.id === $assinaturaSlug ||
        override.produtoID === $assinaturaSlug ||
        override.slug === $assinaturaSlug,
    );

    if (dadosOverride) {
      return { ...produto, ...dadosOverride } as Produto;
    }

    return produto;
  };

  async function recuperarInfoAssinatura(assinaturaID: string) {
    try {
      setCarregandoAssinatura(true);
      const produtoFromServer = await recuperarProduto(assinaturaID);
      setAssinatura(aplicarOverrideAdmin(produtoFromServer));
    } catch (error) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setCarregandoAssinatura(false);
    }
  }

  useEffect(() => {
    if (!$assinaturaSlug || carregandoAssinatura) return;

    const assinaturaNaHome = assinaturas?.find(
      (produto) => produto.slug === $assinaturaSlug,
    );

    if (assinaturaNaHome) {
      setAssinatura(aplicarOverrideAdmin(assinaturaNaHome));
    } else {
      recuperarInfoAssinatura($assinaturaSlug);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [$assinaturaSlug]);

  // useEffect(() => {
  //   if (!assinatura) return;
  //   recuperarProdutosRelacionados(assinatura);
  // }, [assinatura]);

  const [planoSelecionado, setPlanoSelecionado] = useState<number>(0);

  function adicionarProdutoASacola() {
    if (!assinatura || !planosAssinatura) return;

    registrarEvento('assinatura_clicou_adicionar_sacola', {
      produtoID: assinatura?.produtoID,
      produto: assinatura?.nome,
      plano: planosAssinatura[planoSelecionado]?.nome,
      planoID: planosAssinatura[planoSelecionado]?.id,
    });
    registrarEvento('add_to_cart', {
      currency: 'BRL',
      value: planosAssinatura[planoSelecionado]?.preco,
      items: [
        {
          item_id: planosAssinatura[planoSelecionado]?.id,
          item_brand: assinatura?.nome,
          item_name: planosAssinatura[planoSelecionado]?.nome,
          item_discount: planosAssinatura[planoSelecionado]?.desconto,
          quantity: 1,
        },
      ],
    });

    enviarEventosMetaTrack('add_cart');

    adicionarItemASacola({
      produto: assinatura,
      opcaoSelecionadaProduto: planosAssinatura[planoSelecionado],
      quantidade: 1,
    });
  }

  function assinarAgora() {
    if (!assinatura || !planosAssinatura) return;

    registrarEvento('assinatura_clicou_assinar_agora', {
      produtoID: assinatura?.produtoID,
      assinatura: assinatura?.nome,
      plano: planosAssinatura[planoSelecionado]?.nome,
      planoID: planosAssinatura[planoSelecionado]?.id,
    });
    registrarEvento('add_to_cart', {
      currency: 'BRL',
      value: planosAssinatura[planoSelecionado]?.preco,
      items: [
        {
          item_id: planosAssinatura[planoSelecionado]?.id,
          item_brand: assinatura?.nome,
          item_name: planosAssinatura[planoSelecionado]?.nome,
          discount: planosAssinatura[planoSelecionado]?.desconto,
        },
      ],
    });

    enviarEventosMetaTrack('pay_now');

    adicionarItemASacola(
      {
        produto: assinatura,
        opcaoSelecionadaProduto: planosAssinatura[planoSelecionado],
        quantidade: 1,
      },
      false,
    );

    if (!existeComboNaSacola(itens)) {
      router.push('/sacola', { scroll: true });
    }
  }

  const valorTotal = useMemo(() => {
    if (!assinatura || !planosAssinatura) return 0 * 1;

    if (assinatura.desconto > 0) {
      return planosAssinatura[planoSelecionado].preco - assinatura.desconto;
    }

    return planosAssinatura[planoSelecionado].preco * 1;
  }, [assinatura, planoSelecionado, planosAssinatura]);

  const assinaturaEmVigor = useMemo(() => {
    return planosAssinatura.some((plano) => plano.assinado);
  }, [planosAssinatura]);

  const imagensAssinatura: ImagensDestaqueProduto[] = useMemo(() => {
    if (
      !assinatura ||
      !assinatura.imagensDestaque ||
      assinatura.imagensDestaque.length === 0
    )
      return [];
    return [...assinatura.imagensDestaque].sort((a, b) => a.ordem - b.ordem);
  }, [assinatura]);

  const jaPosterAssinaturaRef = useRef(false);

  const [
    carregandoTitulosDisponiveisAssinatura,
    setCarregandoTituloDisponiveisAssinatura,
  ] = useState(false);

  const [titulosDisponiveisAssinatura, setTitulosDisponiveisAssinatura] =
    useState<ConteudoRecomendacao[]>();

  async function recuperarTitulosDisponiveisAssinatura(categoriaID: string) {
    try {
      setCarregandoTituloDisponiveisAssinatura(true);
      const retornoRecuperarConteudoRecomendacaoEspecifica =
        await recuperarConteudoRecomendacaoEspecifica({
          categoriaID,
        });

      setTitulosDisponiveisAssinatura(
        retornoRecuperarConteudoRecomendacaoEspecifica,
      );
    } catch (error: unknown) {
      if (error instanceof Error) {
        mostrarSnackbar({
          color: 'error',
          highContrast: true,
          mensagem: error.message ?? '',
          size: 1,
          variant: 'solid',
        });
      }
    } finally {
      setCarregandoTituloDisponiveisAssinatura(false);
    }
  }

  useEffect(() => {
    if (
      !jaPosterAssinaturaRef.current &&
      assinatura &&
      !!assinatura.categoriaDestaque &&
      assinatura.categoriaDestaque?.length > 1
    ) {
      jaPosterAssinaturaRef.current = true;
      recuperarTitulosDisponiveisAssinatura(assinatura.categoriaDestaque);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assinatura]);

  const bannerDestaqueAssinatura: string | StaticImageData = useMemo(() => {
    const assinaturaRecord = assinatura as unknown as Record<
      string,
      string | StaticImageData
    >;
    if (assinaturaRecord?.['imagemDestaque.0.imagemUrl']) {
      return assinaturaRecord['imagemDestaque.0.imagemUrl'];
    }

    if (assinatura?.imagensDestaque && assinatura?.imagensDestaque.length) {
      const bannerPaginaAssinatura = assinatura?.imagensDestaque.filter(
        (imagem) =>
          imagem.ordem === ImagensProdutoDestaqueTipo.BANNER_PAGINA_PRODUTO,
      );

      console.log(bannerPaginaAssinatura);

      if (bannerPaginaAssinatura.length) {
        return bannerPaginaAssinatura?.[0].imagemUrl;
      }
    }

    return assinatura?.imagem || '';
  }, [assinatura]);

  const iconeServicoAssinatura: string = useMemo(() => {
    if (assinatura?.imagensDestaque && assinatura?.imagensDestaque.length) {
      const iconesAssinatura = assinatura?.imagensDestaque.filter(
        (imagem) => imagem.ordem === ImagensProdutoDestaqueTipo.ICONE,
      );

      if (iconesAssinatura.length > 0) {
        return iconesAssinatura[0].imagemUrl;
      }
    }

    return assinatura?.imagemThumb || '';
  }, [assinatura]);

  const gradienteReduzido: boolean = useMemo(() => {
    const produtosGradienteReduzido = [deezerID];

    if (produtosGradienteReduzido.some((p) => p === assinatura?.produtoID))
      return true;

    return false;
  }, [assinatura]);

  const discosDisponiveis = useMemo(() => {
    const deveMostrarDiscos = [deezerID];
    if (deveMostrarDiscos.some((p) => p === assinatura?.produtoID)) {
      return [disco1, disco2, disco3, disco4, disco5];
    }

    return null;
  }, [assinatura]);

  useDesactivateOfferRedirect({ offer: assinatura ?? ({} as Produto), router });

  return {
    assinatura,
    planoSelecionado,
    setPlanoSelecionado,
    adicionarProdutoASacola,
    valorTotal,
    assinarAgora,
    planosAssinatura,
    tipoAssinatura,
    setTipoAssintura,
    assinaturaEmVigor,
    imagensAssinatura,
    titulosDisponiveisAssinatura: titulosDisponiveisAssinatura?.slice(0, 5),
    carregandoTitulosDisponiveisAssinatura,
    bannerDestaqueAssinatura,
    iconeServicoAssinatura,
    gradienteReduzido,
    discosDisponiveis,
  };
}

export type useServicoAssinaturaPageHookTypeReturn = ReturnType<
  typeof useServicoAssinaturaPageHook
>;

export default useServicoAssinaturaPageHook;
